/**
 * Documents rattachés à un GRV : liste et dépôt.
 *
 * Le téléchargement ouvre l'URL pré-signée dans un nouvel onglet plutôt que de streamer le fichier à
 * travers l'application : c'est une redirection vers S3, pas une donnée à afficher.
 *
 * Pas de statut affiché (`docs/DECISIONS.md` D84) : un document existe ou a été supprimé
 * (« Supprimer », réservé à l'administrateur — jamais une suppression physique, D15/D36/D39).
 *
 * L'onglet « Check-list » a disparu (`docs/DECISIONS.md` D90) : « Check-list » est une option de
 * plus dans le menu déroulant « Type de document », à côté de celles du référentiel `type_document`
 * — la sélectionner remplace le formulaire de dépôt par la check-list elle-même
 * (`OngletChecklist.tsx`, réutilisé tel quel), plutôt que de dupliquer son cycle de vie ici.
 */
import { useMemo, useState } from "react";

import { useReferentiel } from "../admin/requetes";
import { ErreurApi } from "../../shared/api/erreurs";
import { OngletChecklist } from "../maintenance/OngletChecklist";
import { ouvrirTelechargement } from "../../shared/composants/telechargement";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { formaterDateSeule } from "../../shared/composants/formatage";
import {
  useDeposerDocument,
  useDocuments,
  useSupprimerDocument,
  useUrlDeTelechargement,
} from "./requetes";

// Pas un code du référentiel `type_document` : une check-list n'est pas un document déposé, elle a
// son propre cycle de vie (`maintenance/service.py`). Choisi pour ne jamais entrer en collision
// avec un code de référentiel (`checklist_question` est le référentiel des questions, pas des types).
const TYPE_CHECKLIST = "checklist";

function ilYAJours(jours: number): string {
  const date = new Date();
  date.setDate(date.getDate() - jours);
  return date.toISOString().slice(0, 10);
}

export function OngletDocuments({
  numero,
  peutDeposer,
  peutSupprimer,
  voitChecklist,
}: {
  numero: string;
  peutDeposer: boolean;
  peutSupprimer: boolean;
  voitChecklist: boolean;
}) {
  const requete = useDocuments(numero);
  const requeteTypes = useReferentiel("type_document");
  const deposer = useDeposerDocument(numero);
  const supprimer = useSupprimerDocument(numero);
  const telecharger = useUrlDeTelechargement();

  const [typeDocument, definirType] = useState("");
  const [dateFichier, definirDateFichier] = useState(() => ilYAJours(0));
  const [dateFinValidite, definirDateFinValidite] = useState("");
  const [descriptif, definirDescriptif] = useState("");
  const [fichier, definirFichier] = useState<File | null>(null);
  const [aSupprimer, definirASupprimer] = useState<string | null>(null);

  const typesDisponibles = useMemo(() => {
    const valeurs = requeteTypes.data?.data ?? {};
    return Object.entries(valeurs)
      .filter(([, valeur]) => valeur.actif !== false)
      .sort(([, a], [, b]) => (a.ordre ?? 0) - (b.ordre ?? 0));
  }, [requeteTypes.data]);

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault();
    if (!fichier || !typeDocument) return;
    await deposer.mutateAsync({
      demande: {
        type_document: typeDocument,
        nom_fichier: fichier.name,
        content_type: fichier.type || "application/octet-stream",
        date_fichier: dateFichier,
        date_fin_validite: dateFinValidite || null,
        descriptif: descriptif || null,
      },
      fichier,
    });
    definirType("");
    definirDateFichier(ilYAJours(0));
    definirDateFinValidite("");
    definirDescriptif("");
    definirFichier(null);
  }

  async function confirmerSuppression(documentId: string) {
    await supprimer.mutateAsync(documentId);
    definirASupprimer(null);
  }

  function ouvrirLeTelechargement(documentId: string): void {
    ouvrirTelechargement(async () => (await telecharger.mutateAsync(documentId)).data.url_telechargement);
  }

  return (
    <div>
      {peutDeposer && (
        <div className="formulaire-ligne">
          <label htmlFor="type-document">Type de document</label>
          <select
            id="type-document"
            required
            value={typeDocument}
            onChange={(e) => definirType(e.target.value)}
          >
            <option value="" disabled>
              Choisir…
            </option>
            {typesDisponibles.map(([code, valeur]) => (
              <option key={code} value={code}>
                {valeur.libelle}
              </option>
            ))}
            {voitChecklist && <option value={TYPE_CHECKLIST}>Check-list</option>}
          </select>
        </div>
      )}

      {peutDeposer && typeDocument === TYPE_CHECKLIST && (
        <OngletChecklist numero={numero} peutAdministrer={peutDeposer} />
      )}

      {peutDeposer && typeDocument !== TYPE_CHECKLIST && (
        <form className="formulaire-ligne" onSubmit={(e) => void soumettre(e)}>
          <label htmlFor="descriptif-document">Descriptif</label>
          <input
            id="descriptif-document"
            value={descriptif}
            onChange={(e) => definirDescriptif(e.target.value)}
          />
          <label htmlFor="date-fichier">Date du fichier</label>
          <input
            id="date-fichier"
            type="date"
            required
            value={dateFichier}
            onChange={(e) => definirDateFichier(e.target.value)}
          />
          <label htmlFor="date-fin-validite">Date de fin de validité</label>
          <input
            id="date-fin-validite"
            type="date"
            value={dateFinValidite}
            onChange={(e) => definirDateFinValidite(e.target.value)}
          />
          <label htmlFor="fichier-document">Fichier</label>
          <input
            id="fichier-document"
            type="file"
            required
            onChange={(e) => definirFichier(e.target.files?.[0] ?? null)}
          />
          <button type="submit" disabled={deposer.isPending || !fichier || !typeDocument}>
            Déposer
          </button>
          {deposer.isError && (
            <span role="alert">
              {deposer.error instanceof ErreurApi
                ? deposer.error.message
                : ((deposer.error as Error | null)?.message ?? "Le dépôt a échoué.")}
            </span>
          )}
        </form>
      )}

      {requete.isPending && <Chargement libelle="Chargement des documents…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}
      {requete.data && requete.data.data.length === 0 && <Vide libelle="Aucun document déposé." />}
      {requete.data && requete.data.data.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Fichier</th>
              <th scope="col">Descriptif</th>
              <th scope="col">Date du fichier</th>
              <th scope="col">Fin de validité</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {requete.data.data.map((document) => (
              <tr key={document.document_id}>
                <td>{document.type_document}</td>
                <td>{document.nom_fichier}</td>
                <td>{document.descriptif || "—"}</td>
                <td>{formaterDateSeule(document.date_fichier)}</td>
                <td>{formaterDateSeule(document.date_fin_validite)}</td>
                <td className="formulaire-actions">
                  <button type="button" onClick={() => ouvrirLeTelechargement(document.document_id)}>
                    Télécharger
                  </button>
                  {peutSupprimer &&
                    (aSupprimer === document.document_id ? (
                      <>
                        <span>Supprimer ce document ?</span>
                        <button
                          type="button"
                          onClick={() => void confirmerSuppression(document.document_id)}
                          disabled={supprimer.isPending}
                        >
                          Confirmer
                        </button>
                        <button type="button" onClick={() => definirASupprimer(null)}>
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => definirASupprimer(document.document_id)}>
                        Supprimer
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
